'use client'

import { useEffect, useState } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { toast } from '@/lib/toast'
import { navigateUrl, resolveRedirect } from '@/utils/client-request'
import { useAuth } from './useAuth'
import { useCart } from './useCart'

type UseLoginFormArgs = {
  defaultRedirect: string
  refreshAfter?: boolean
  clearErrorParam?: boolean
}

export function useLoginForm({ defaultRedirect, refreshAfter = false, clearErrorParam = false }: UseLoginFormArgs) {
  const { login } = useAuth()
  const { fetchCart } = useCart()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get('url')

  const [emailOrPhone, setEmailOrPhone] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (searchParams.has('error')) {
      setLoading(false)
      toast.error('Бүртгэл олдсонгүй')
      if (clearErrorParam) router.replace(pathname)
    }
  }, [searchParams])

  const handleLogin = async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.()
    if (!emailOrPhone) return toast.error('Имэйл хаяг эсвэл утасны дугаар хоосон байна')
    if (!password) return toast.error('Нууц үг хоосон байна')
    setLoading(true)
    const response = await login({ emailOrPhone, password })
    setLoading(false)
    if (!response.ok) return toast.error(response.error || 'Нэвтрэхэд алдаа гарлаа')
    toast.success('Амжилттай нэвтэрлээ')
    setTimeout(() => {
      const safeRedirect = resolveRedirect(redirectUrl, pathname)
      const target = safeRedirect || navigateUrl(defaultRedirect, pathname)
      if (process.env.NEXT_PUBLIC_ENV === 'production') {
        window.location.href = `${window.location.protocol}//${window.location.host}${target}`
        return
      }
      if (safeRedirect) {
        router.push(safeRedirect)
      } else {
        fetchCart()
        router.push(target)
        if (refreshAfter) router.refresh()
      }
    }, 100)
  }

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') handleLogin(event)
  }

  const routeToRegister = () => {
    const target = redirectUrl ? navigateUrl(`/register?url=${redirectUrl}`, pathname) : navigateUrl('/register', pathname)
    if (process.env.NEXT_PUBLIC_ENV === 'production') {
      window.location.href = `${window.location.protocol}//${window.location.host}${target}`
      return
    }
    router.push(target)
  }

  return {
    emailOrPhone, setEmailOrPhone,
    password, setPassword,
    loading,
    handleLogin, handleKeyDown,
    routeToRegister,
    redirectUrl,
  }
}
